<script setup lang="ts">
import Popover from 'primevue/popover'
import { computed, ref } from 'vue'

import { useI18n, type MessageKey } from '@/app/i18n'
import { navTarget, type NavItem } from '@/app/nav/collect'
import { useCountsStore } from '@/app/stores/counts'
import { useCurrentSoftware } from '@/shared/composables/useCurrentSoftware'
import AppBadge from '@/shared/ui/AppBadge.vue'
import AppIcon from '@/shared/ui/AppIcon.vue'

import { alertEntries } from './navbar-items'
import { useShellMenu } from './useShellMenu'

/**
 * The navbar's bell: the badges of the current software's menu that wait on the user (unread messages, unpaid
 * invoices, leaves and timesheets to approve — `navbar-items.ts`), summed on the bell and listed in a tray, each row a
 * link to its page. It replaces the inbox icon of the old bar (2026-10); Messages keeps its menu entry.
 */
const { t } = useI18n()
const counts = useCountsStore()
const { software } = useCurrentSoftware()
const menu = useShellMenu()
const popover = ref<InstanceType<typeof Popover> | null>(null)
const open = ref(false)

/** The lit badges with their page; a badge whose page resolves to no route for this software is not listed. */
const alerts = computed(() =>
  alertEntries(menu.value, (key) => counts.badge(key)).flatMap((alert) => {
    const to = software.value ? navTarget(alert.entry.item, software.value) : undefined
    return to ? [{ ...alert, to }] : []
  }),
)
const total = computed(() => alerts.value.reduce((sum, alert) => sum + alert.count, 0))
// Titles are message keys declared with keys.t() in the nav files.
// eslint-disable-next-line @intlify/vue-i18n/no-dynamic-keys
const label = (item: NavItem): string => t(item.title as MessageKey)
</script>

<template>
  <button
    type="button"
    class="navbar-round"
    :aria-label="total ? `${t('shell.navbar.action.alerts')} (${total})` : t('shell.navbar.action.alerts')"
    aria-haspopup="true"
    :aria-expanded="open"
    data-testid="navbar-alerts"
    @click="popover?.toggle($event)"
  >
    <AppIcon name="BellIcon" :size="20" :badge="total" />
  </button>
  <Popover
    ref="popover"
    :base-z-index="1100"
    :pt="{ root: { class: 'shell-popover navbar-panel navbar-alerts__panel', 'data-testid': 'navbar-alerts-list' } }"
    @show="open = true"
    @hide="open = false"
  >
    <div class="navbar-panel__heading">{{ t('shell.navbar.action.alerts') }}</div>
    <ul v-if="alerts.length" class="navbar-panel__list">
      <li v-for="alert in alerts" :key="alert.entry.item.id">
        <RouterLink
          :to="alert.to"
          class="navbar-panel__row"
          :data-alert="alert.entry.item.id"
          @click="popover?.hide()"
        >
          <AppIcon
            :name="alert.entry.item.icon || alert.entry.parent?.icon || 'CircleIcon'"
            :size="18"
            class="navbar-panel__row-icon"
          />
          <span class="navbar-panel__row-text">
            <span v-if="alert.entry.parent" class="navbar-panel__row-hint">{{ label(alert.entry.parent) }}</span>
            <span class="navbar-panel__row-title">{{ label(alert.entry.item) }}</span>
          </span>
          <AppBadge :value="alert.count" variant="danger" />
        </RouterLink>
      </li>
    </ul>
    <p v-else class="navbar-panel__empty">{{ t('shell.navbar.state.noAlerts') }}</p>
  </Popover>
</template>
